import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    CCard,
    CCardBody,
    CCardHeader,
    CForm,
    CFormInput,
    CFormLabel,
    CFormTextarea,
    CFormSelect,
    CButton,
    CRow,
    CCol,
    CFormSwitch,
    CBadge,
    CAlert,
    CSpinner,
    CTable,
    CTableHead,
    CTableRow,
    CTableHeaderCell,
    CTableBody,
    CTableDataCell,
} from '@coreui/react-pro';
// Ajusta las rutas relativas según la ubicación de este archivo
import { getStoreProfile, updateStoreProfile, uploadLogo } from '../../services/DomicilappService';
import { StoreProfile, StoreSocialLink } from '../../types/store';
import { useStore } from '../../contexts/StoreContext';

interface Props {
    storeId: string;
}

type Feedback = { type: 'success' | 'danger'; message: string } | null;

const PLATFORMS = ['Instagram', 'Facebook', 'TikTok', 'WhatsApp', 'X (Twitter)', 'Sitio Web'];

const isValidUrl = (value: string) => {
    try {
        const u = new URL(value);
        return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
        return false;
    }
};

// Convención del backend: en error, data.error (string, array o errores de validación por campo).
// processErrorResponse devuelve undefined cuando hubo 401 (userLogout ya redirige).
const hasError = (res: any) => !res || !res.data || !!res.data.error;

const getErrorMessage = (res: any): string => {
    const err = res?.data?.error;
    if (typeof err === 'string') return err;
    if (err && typeof err === 'object') {
        const first: any = Object.values(err)[0];
        if (Array.isArray(first) && typeof first[0] === 'string') return first[0];
        if (typeof first === 'string') return first;
    }
    return 'No se pudo completar la operación. Intenta de nuevo.';
};

// export const StoreProfilePage: React.FC<Props> = () => {
    export const StoreProfilePage: React.FC = () => {
    const [profile, setProfile] = useState<StoreProfile | null>(null);
    const [socialLinks, setSocialLinks] = useState<StoreSocialLink[]>([]);
    const { storeId } = useStore();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [togglingOpen, setTogglingOpen] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [feedback, setFeedback] = useState<Feedback>(null);
    const [dirty, setDirty] = useState(false);

    const [newPlatform, setNewPlatform] = useState(PLATFORMS[0]);
    const [newUrl, setNewUrl] = useState('');
    const [newUrlError, setNewUrlError] = useState('');

    const fileInputRef = useRef<HTMLInputElement>(null);

    const applyProfile = (p: StoreProfile) => {
        setProfile(p);
        setSocialLinks(p.social_links ?? []);
        setDirty(false);
    };

    // ---------- Cargar ----------
    const loadProfile = useCallback(async () => {
        setLoading(true);
        setFeedback(null);
        try {
            const res = await getStoreProfile(storeId);
            if (hasError(res)) {
                setFeedback({ type: 'danger', message: getErrorMessage(res) });
            } else {
                applyProfile(res.data as StoreProfile);
            }
        } catch {
            setFeedback({ type: 'danger', message: 'No se pudo cargar el perfil. Revisa tu conexión.' });
        } finally {
            setLoading(false);
        }
    }, [storeId]);

    useEffect(() => {
        loadProfile();
    }, [loadProfile]);

    // ---------- Edición ----------
    type EditableField = 'name' | 'description' | 'email' | 'phone' | 'address';

    const updateField = (key: EditableField, value: string) => {
        setProfile((prev) => (prev ? { ...prev, [key]: value } : prev));
        setDirty(true);
    };

    // El estado abierto/cerrado se guarda al instante (es una acción operativa), con reversión si falla
    const handleToggleOpen = async (checked: boolean) => {
        if (!profile) return;
        const previous = profile.is_open;
        setProfile({ ...profile, is_open: checked });
        setTogglingOpen(true);
        setFeedback(null);
        try {
            const res = await updateStoreProfile(storeId, { is_open: checked });
            if (hasError(res)) {
                setProfile((p) => (p ? { ...p, is_open: previous } : p));
                setFeedback({ type: 'danger', message: getErrorMessage(res) });
            }
        } catch {
            setProfile((p) => (p ? { ...p, is_open: previous } : p));
            setFeedback({ type: 'danger', message: 'No se pudo cambiar el estado de la tienda.' });
        } finally {
            setTogglingOpen(false);
        }
    };

    // ---------- Redes sociales ----------
    const handleAddSocial = () => {
        const url = newUrl.trim();
        if (!isValidUrl(url)) {
            setNewUrlError('Ingresa una URL válida que empiece con http:// o https://');
            return;
        }
        setNewUrlError('');
        setSocialLinks((prev) => [...prev, { id: `tmp-${Date.now()}`, platform: newPlatform, url }]);
        setNewUrl('');
        setDirty(true);
    };

    const handleRemoveSocial = (id: StoreSocialLink['id']) => {
        setSocialLinks((prev) => prev.filter((item) => item.id !== id));
        setDirty(true);
    };

    // ---------- Guardar ----------
    const handleSave = async () => {
        if (!profile) return;
        if (!profile.name.trim()) {
            setFeedback({ type: 'danger', message: 'El nombre del establecimiento es obligatorio.' });
            return;
        }

        setSaving(true);
        setFeedback(null);
        try {
            const res = await updateStoreProfile(storeId, {
                name: profile.name.trim(),
                description: profile.description,
                email: profile.email,
                phone: profile.phone,
                address: profile.address,
                social_links: socialLinks.map(({ platform, url }) => ({ platform, url })),
            });
            if (hasError(res)) {
                setFeedback({ type: 'danger', message: getErrorMessage(res) });
            } else {
                applyProfile(res.data as StoreProfile); // el backend devuelve el perfil actualizado
                setFeedback({ type: 'success', message: 'Perfil guardado correctamente.' });
            }
        } catch {
            setFeedback({ type: 'danger', message: 'No se pudo guardar el perfil. Revisa tu conexión.' });
        } finally {
            setSaving(false);
        }
    };

    // ---------- Logo (reutiliza uploadLogo -> account/{id}/logo) ----------
    const handleLogoSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setFeedback({ type: 'danger', message: 'El logo debe ser una imagen (PNG, JPG o WEBP).' });
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            setFeedback({ type: 'danger', message: 'El logo no puede pesar más de 2 MB.' });
            return;
        }

        setUploadingLogo(true);
        setFeedback(null);
        try {
            const res: any = await uploadLogo(Number(storeId), file);
            const url = res?.data?.url;
            if (url) {
                setProfile((p) => (p ? { ...p, logo: url } : p));
                setFeedback({ type: 'success', message: 'Logo actualizado.' });
            } else {
                setFeedback({ type: 'danger', message: getErrorMessage(res) });
            }
        } catch {
            setFeedback({ type: 'danger', message: 'No se pudo subir el logo.' });
        } finally {
            setUploadingLogo(false);
        }
    };

    const stars = useMemo(() => {
        const full = Math.round(profile?.rating ?? 0);
        return '★'.repeat(full) + '☆'.repeat(5 - full);
    }, [profile?.rating]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center py-5">
                <CSpinner />
            </div>
        );
    }

    if (!profile) {
        return (
            <>
                {feedback && <CAlert color={feedback.type}>{feedback.message}</CAlert>}
                <CButton color="dark" className="text-white" onClick={loadProfile}>
                    Reintentar
                </CButton>
            </>
        );
    }

    const initial = profile.name.trim().charAt(0).toUpperCase() || '?';

    return (
        <>
            {feedback && (
                <CAlert color={feedback.type} dismissible onClose={() => setFeedback(null)}>
                    {feedback.message}
                </CAlert>
            )}

            {/* RESUMEN */}
            <CRow className="mb-4 g-3">
                <CCol md={6}>
                    <CCard className="border border-secondary-subtle shadow-none bg-body rounded-3 h-100">
                        <CCardBody className="d-flex align-items-center justify-content-between p-3 text-body">
                            <div>
                                <span className="text-body-secondary small fw-semibold">Calificación de la tienda</span>
                                <div className="d-flex align-items-center mt-1">
                                    <h3 className="fw-bold mb-0 me-2 text-body">
                                        {profile.total_reviews > 0 ? profile.rating.toFixed(1) : '—'}
                                    </h3>
                                    <span className="fs-5 text-warning me-2">{stars}</span>
                                    <small className="text-body-secondary">({profile.total_reviews} opiniones)</small>
                                </div>
                            </div>
                            {profile.rating >= 4.5 && profile.total_reviews > 0 && (
                                <CBadge color="warning" className="text-dark border border-warning-subtle rounded-pill px-3 py-2 fw-normal">
                                    Reputación alta
                                </CBadge>
                            )}
                        </CCardBody>
                    </CCard>
                </CCol>

                <CCol md={6}>
                    <CCard className="border border-secondary-subtle shadow-none bg-body rounded-3 h-100">
                        <CCardBody className="p-3 text-body">
                            <span className="text-body-secondary small fw-semibold">Seguidores en la app</span>
                            <div className="d-flex align-items-center mt-1">
                                <h3 className="fw-bold mb-0 me-2 text-body">{profile.followers_count.toLocaleString()}</h3>
                                <small className="text-body-secondary">usuarios te siguen</small>
                            </div>
                        </CCardBody>
                    </CCard>
                </CCol>
            </CRow>

            {/* FORMULARIO */}
            <CCard className="border border-secondary-subtle shadow-none mb-4 bg-body rounded-3">
                <CCardHeader className="bg-body border-bottom border-secondary-subtle py-3 d-flex justify-content-between align-items-center text-body">
                    <h6 className="mb-0 fw-bold">Perfil público y redes sociales</h6>
                    {/* <div className="d-flex align-items-center gap-2">
                        <span className="small text-body-secondary fw-semibold">Recepción de pedidos:</span>
                        <CBadge color={profile.is_open ? 'success' : 'danger'} className="rounded-pill px-3 py-1 fw-normal">
                            {profile.is_open ? 'Abierto' : 'Cerrado'}
                        </CBadge>
                        <CFormSwitch
                            id="store-open-switch"
                            checked={profile.is_open}
                            disabled={togglingOpen}
                            onChange={(e) => handleToggleOpen(e.target.checked)}
                        />
                    </div> */}
                </CCardHeader>

                <CCardBody className="p-4 bg-body text-body">
                    {/* LOGO */}
                    <div className="mb-4 text-center border border-secondary-subtle p-4 rounded-3 bg-body-tertiary">
                        {profile.logo ? (
                            <img
                                src={profile.logo}
                                alt="Logo de la tienda"
                                className="rounded-circle border border-secondary-subtle mb-2"
                                style={{ width: 100, height: 100, objectFit: 'cover' }}
                            />
                        ) : (
                            <div
                                className="rounded-circle border border-secondary-subtle mb-2 mx-auto d-flex align-items-center justify-content-center bg-body fs-2 fw-bold text-body-secondary"
                                style={{ width: 100, height: 100 }}
                            >
                                {initial}
                            </div>
                        )}
                        <div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                hidden
                                onChange={handleLogoSelected}
                            />
                            <CButton
                                type="button"
                                size="sm"
                                color="dark"
                                variant="outline"
                                className="border-secondary-subtle text-body"
                                disabled={uploadingLogo}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                {uploadingLogo ? <CSpinner size="sm" /> : 'Cambiar logo'}
                            </CButton>
                        </div>
                    </div>

                    <CForm
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleSave();
                        }}
                    >
                        <span className="fw-semibold mb-3 text-body-secondary d-block small">Datos principales</span>
                        <CRow className="g-3 mb-4">
                            <CCol md={6}>
                                <CFormLabel className="small fw-semibold text-body-secondary mb-1">Nombre del establecimiento</CFormLabel>
                                <CFormInput
                                    className="bg-body text-body border-secondary-subtle"
                                    value={profile.name}
                                    required
                                    onChange={(e) => updateField('name', e.target.value)}
                                />
                            </CCol>
                            {/* <CCol md={6}>
                                <CFormLabel className="small fw-semibold text-body-secondary mb-1">Correo de contacto</CFormLabel>
                                <CFormInput
                                    type="email"
                                    className="bg-body text-body border-secondary-subtle"
                                    value={profile.email ?? ''}
                                    onChange={(e) => updateField('email', e.target.value)}
                                />
                            </CCol> */}
                            {/* <CCol md={6}>
                                <CFormLabel className="small fw-semibold text-body-secondary mb-1">Teléfono de atención</CFormLabel>
                                <CFormInput
                                    type="tel"
                                    className="bg-body text-body border-secondary-subtle"
                                    value={profile.phone ?? ''}
                                    onChange={(e) => updateField('phone', e.target.value)}
                                />
                            </CCol> */}
                            <CCol md={6}>
                                <CFormLabel className="small fw-semibold text-body-secondary mb-1">Dirección del local</CFormLabel>
                                <CFormInput
                                    className="bg-body text-body border-secondary-subtle"
                                    value={profile.address ?? ''}
                                    onChange={(e) => updateField('address', e.target.value)}
                                />
                                {!profile.has_coordinates && !dirty && (
                                    <small className="text-warning-emphasis d-block mt-1">
                                        Esta dirección aún no tiene ubicación verificada en el mapa. Revisa que incluya calle y número.
                                    </small>
                                )}
                            </CCol>
                            <CCol md={12}>
                                <CFormLabel className="small fw-semibold text-body-secondary mb-1">Descripción o eslogan</CFormLabel>
                                <CFormTextarea
                                    rows={3}
                                    maxLength={1000}
                                    className="bg-body text-body border-secondary-subtle"
                                    value={profile.description ?? ''}
                                    onChange={(e) => updateField('description', e.target.value)}
                                />
                            </CCol>
                        </CRow>

                        <hr className="my-4 border-secondary-subtle" />

                        {/* REDES SOCIALES */}
                        <span className="fw-semibold mb-2 text-body-secondary d-block small">
                            Redes sociales (visibles en la app móvil)
                        </span>
                        <CAlert color="secondary" className="mb-3 border border-secondary-subtle text-body-secondary bg-body-tertiary small">
                            Los cambios en las redes se aplican al guardar el perfil.
                        </CAlert>

                        <CRow className="g-2 mb-3 align-items-start">
                            <CCol md={4}>
                                <CFormLabel className="small fw-semibold text-body-secondary mb-1">Plataforma</CFormLabel>
                                <CFormSelect
                                    className="bg-body text-body border-secondary-subtle"
                                    value={newPlatform}
                                    onChange={(e) => setNewPlatform(e.target.value)}
                                >
                                    {PLATFORMS.map((p) => (
                                        <option key={p} value={p}>
                                            {p}
                                        </option>
                                    ))}
                                </CFormSelect>
                            </CCol>
                            <CCol md={6}>
                                <CFormLabel className="small fw-semibold text-body-secondary mb-1">URL del perfil</CFormLabel>
                                <CFormInput
                                    className="bg-body text-body border-secondary-subtle"
                                    placeholder="https://..."
                                    value={newUrl}
                                    invalid={!!newUrlError}
                                    feedbackInvalid={newUrlError}
                                    onChange={(e) => {
                                        setNewUrl(e.target.value);
                                        if (newUrlError) setNewUrlError('');
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddSocial();
                                        }
                                    }}
                                />
                            </CCol>
                            <CCol md={2} className="pt-md-4">
                                <CButton type="button" color="dark" className="w-100 fw-medium text-white mt-md-1" onClick={handleAddSocial}>
                                    + Agregar
                                </CButton>
                            </CCol>
                        </CRow>

                        {socialLinks.length > 0 && (
                            <div className="border border-secondary-subtle rounded-3 mb-4 overflow-hidden">
                                <CTable align="middle" responsive hover className="mb-0 text-body">
                                    <CTableHead className="bg-body-tertiary border-bottom border-secondary-subtle">
                                        <CTableRow>
                                            <CTableHeaderCell className="text-body-secondary small fw-semibold">Plataforma</CTableHeaderCell>
                                            <CTableHeaderCell className="text-body-secondary small fw-semibold">Enlace</CTableHeaderCell>
                                            <CTableHeaderCell className="text-body-secondary small fw-semibold text-end">Acción</CTableHeaderCell>
                                        </CTableRow>
                                    </CTableHead>
                                    <CTableBody>
                                        {socialLinks.map((item) => (
                                            <CTableRow key={item.id} className="border-bottom border-secondary-subtle">
                                                <CTableDataCell className="fw-semibold text-body">{item.platform}</CTableDataCell>
                                                <CTableDataCell>
                                                    <a href={item.url} target="_blank" rel="noreferrer" className="text-body text-decoration-underline">
                                                        {item.url}
                                                    </a>
                                                </CTableDataCell>
                                                <CTableDataCell className="text-end">
                                                    <CButton
                                                        type="button"
                                                        size="sm"
                                                        color="danger"
                                                        variant="ghost"
                                                        onClick={() => handleRemoveSocial(item.id)}
                                                    >
                                                        Eliminar
                                                    </CButton>
                                                </CTableDataCell>
                                            </CTableRow>
                                        ))}
                                    </CTableBody>
                                </CTable>
                            </div>
                        )}

                        <div className="d-flex justify-content-end align-items-center gap-3">
                            {dirty && <small className="text-body-secondary">Tienes cambios sin guardar</small>}
                            <CButton type="submit" color="dark" className="px-4 fw-medium text-white" disabled={saving || !dirty}>
                                {saving ? <CSpinner size="sm" /> : 'Guardar cambios'}
                            </CButton>
                        </div>
                    </CForm>
                </CCardBody>
            </CCard>
        </>
    );
};

export default StoreProfilePage;